/**
 * Iter 253z-11 · Performance Marketing · Campaigns page.
 *
 * Table of campaigns across every connected ad account with filters
 * (brand, account, status), inline pause/resume, daily-budget edit,
 * duplicate action. 7-day KPI columns pulled from perf_insights_daily.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Pause, Copy, Save, Loader2, Filter, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const fmtInt = (n) => (Number(n) || 0).toLocaleString();
const fmtMoney = (n, ccy = "USD") => `${ccy} ${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct = (n) => `${(Number(n) || 0).toFixed(2)}%`;

export default function PerformanceCampaigns() {
  const { activeWorkspaceId } = useAuth();
  const [searchParams] = useSearchParams();
  const brandFromUrl = searchParams.get("brand") || "";
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [busy, setBusy] = useState(true);
  const [fBrand, setFBrand] = useState(brandFromUrl);
  const [fAccount, setFAccount] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fQuery, setFQuery] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [editingBudgetId, setEditingBudgetId] = useState(null);
  const [budgetDraft, setBudgetDraft] = useState("");

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const [c, a, b] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/perf/campaigns`, {
          params: {
            brand_id: fBrand || undefined,
            account_id: fAccount || undefined,
            status: fStatus || undefined,
          },
        }),
        api.get(`/workspaces/${activeWorkspaceId}/perf/ad-accounts`),
        api.get(`/workspaces/${activeWorkspaceId}/brands`).catch(() => ({ data: [] })),
      ]);
      setRows(c.data || []);
      setAccounts(a.data || []);
      setBrands(b.data || []);
    } catch (e) { toast.error("Failed to load campaigns"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, fBrand, fAccount, fStatus]);

  const filtered = useMemo(() => {
    if (!fQuery.trim()) return rows;
    const q = fQuery.toLowerCase();
    return rows.filter((r) => r.name.toLowerCase().includes(q) || r.objective?.toLowerCase().includes(q));
  }, [rows, fQuery]);

  const totals = useMemo(() => filtered.reduce((acc, r) => {
    acc.spend += Number(r.spend_7d) || 0;
    acc.impressions += Number(r.impressions_7d) || 0;
    acc.clicks += Number(r.clicks_7d) || 0;
    acc.conversions += Number(r.conversions_7d) || 0;
    return acc;
  }, { spend: 0, impressions: 0, clicks: 0, conversions: 0 }), [filtered]);

  const toggleStatus = async (r) => {
    setBusyId(r.id);
    try {
      const next = r.status === "ACTIVE" ? "PAUSED" : "ACTIVE";
      await api.post(`/perf/campaigns/${r.id}/status`, { status: next });
      toast.success(`${r.name} · ${next}`);
      setRows((s) => s.map((x) => x.id === r.id ? { ...x, status: next } : x));
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusyId(null); }
  };

  const saveBudget = async (r) => {
    const n = Number(budgetDraft);
    if (!n || n < 100) { toast.error("Budget must be at least 100 (in smallest currency unit)"); return; }
    setBusyId(r.id);
    try {
      await api.post(`/perf/campaigns/${r.id}/budget`, { daily_budget: n });
      toast.success("Budget updated");
      setRows((s) => s.map((x) => x.id === r.id ? { ...x, daily_budget: n } : x));
      setEditingBudgetId(null); setBudgetDraft("");
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusyId(null); }
  };

  const duplicate = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/perf/campaigns/${r.id}/duplicate`);
      toast.success("Duplicated · new copy is PAUSED (safe by default)");
      await load();
    } catch (e) { toast.error("Failed"); }
    finally { setBusyId(null); }
  };

  return (
    <>
      <PageHeader
        eyebrow="Performance Marketing"
        title="Campaigns"
        description="Pause, resume, adjust budgets, or duplicate campaigns across every connected ad account. 7-day KPIs shown inline."
        testid="perf-campaigns-header"
        actions={<Button variant="outline" size="sm" onClick={load} className="rounded-none" data-testid="perf-camp-refresh"><RefreshCw className="w-3 h-3 mr-1" /> Refresh</Button>}
      />
      <div className="px-4 sm:px-6 md:px-10 pb-10 space-y-4">
        <div className="border border-border bg-muted/40 p-3 flex flex-wrap items-center gap-2" data-testid="perf-camp-filterbar">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          <Input placeholder="Search name or objective…" value={fQuery} onChange={(e) => setFQuery(e.target.value)} className="h-8 w-56 text-xs" data-testid="perf-camp-q" />
          <Select value={fBrand || "_all"} onValueChange={(v) => setFBrand(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="perf-camp-brand"><SelectValue placeholder="Brand" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All brands</SelectItem>
              {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fAccount || "_all"} onValueChange={(v) => setFAccount(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-8 w-52 text-xs" data-testid="perf-camp-account"><SelectValue placeholder="Ad account" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All accounts</SelectItem>
              {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fStatus || "_all"} onValueChange={(v) => setFStatus(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="perf-camp-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Any status</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="PAUSED">Paused</SelectItem>
            </SelectContent>
          </Select>
          <div className="ml-auto text-[10px] text-muted-foreground tabular-nums">
            {filtered.length} campaign(s) · Total 7d spend: <b>{fmtMoney(totals.spend)}</b> · Clicks: <b>{fmtInt(totals.clicks)}</b> · Conv: <b>{fmtInt(totals.conversions)}</b>
          </div>
        </div>

        <div className="border border-border" data-testid="perf-camp-table">
          {busy ? <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div>
            : filtered.length === 0 ? <div className="p-8 text-center text-xs text-muted-foreground">No campaigns match. Seed demo data or connect Meta to see live campaigns.</div>
            : (
              <table className="w-full text-xs">
                <thead className="bg-muted/20 text-[10px] uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">Campaign</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Objective</th>
                    <th className="text-right px-3 py-2">Daily budget</th>
                    <th className="text-right px-3 py-2">Spend 7d</th>
                    <th className="text-right px-3 py-2">Impr.</th>
                    <th className="text-right px-3 py-2">Clicks</th>
                    <th className="text-right px-3 py-2">CTR</th>
                    <th className="text-right px-3 py-2">CPC</th>
                    <th className="text-right px-3 py-2">Conv.</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((r) => (
                    <tr key={r.id} data-testid={`perf-camp-row-${r.id}`}>
                      <td className="px-3 py-2 font-medium max-w-xs truncate" title={r.name}>
                        <a href={`/app/performance/campaigns/${r.id}`} className="hover:underline" data-testid={`perf-camp-open-${r.id}`}>{r.name}</a>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`chip text-[9px] ${r.status === "ACTIVE" ? "chip-approved" : ""}`}>{r.status}</span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{r.objective?.replace("OUTCOME_", "").toLowerCase()}</td>
                      <td className="px-3 py-2 text-right">
                        {editingBudgetId === r.id ? (
                          <div className="flex items-center gap-1 justify-end">
                            <Input type="number" value={budgetDraft} onChange={(e) => setBudgetDraft(e.target.value)} className="h-7 w-24 text-xs" data-testid={`perf-camp-budget-input-${r.id}`} />
                            <Button size="sm" variant="outline" className="rounded-none h-7 w-7" onClick={() => saveBudget(r)} disabled={busyId === r.id} data-testid={`perf-camp-budget-save-${r.id}`}>
                              {busyId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            </Button>
                          </div>
                        ) : (
                          <button
                            className="tabular-nums hover:underline"
                            onClick={() => { setEditingBudgetId(r.id); setBudgetDraft(String(r.daily_budget || 0)); }}
                            data-testid={`perf-camp-budget-btn-${r.id}`}
                          >
                            {fmtMoney(r.daily_budget, r.currency)}
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.spend_7d, r.currency)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.impressions_7d)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.clicks_7d)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtPct(r.ctr_7d)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.cpc_7d, r.currency)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.conversions_7d)}</td>
                      <td className="px-3 py-2 flex items-center gap-1 justify-end">
                        <Button size="sm" variant="outline" className="rounded-none h-7 w-7" onClick={() => toggleStatus(r)} disabled={busyId === r.id} data-testid={`perf-camp-toggle-${r.id}`} title={r.status === "ACTIVE" ? "Pause" : "Resume"}>
                          {busyId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : (r.status === "ACTIVE" ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />)}
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-none h-7 w-7" onClick={() => duplicate(r)} disabled={busyId === r.id} data-testid={`perf-camp-dup-${r.id}`} title="Duplicate (as PAUSED)">
                          <Copy className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>
      </div>
    </>
  );
}
